import { useTranslation } from 'react-i18next';

import { customRound, europeanFormatNumber } from '@/helpers';
import { useTechnologies } from '@/hooks';
import { Button, Icon, Typography } from '@/components';
import { useLocation } from '@/features/projects/hooks';

export type GeneralBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function GeneralBox({ normalizedProject, onEdit, isEditable }: GeneralBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapTechnology } = useTechnologies();

  const technologies = normalizedProject.technologies.map((tech: any) => mapTechnology(tech.type)).join(', ');

  
  const totalInstalledCapacity = normalizedProject.technologies.reduce((sum: any, tech: any) => {
    const roundedInstalledCapacityPeak = customRound(tech.details?.item?.installedCapacityPeak);
    const roundedInstallCapacity = customRound(tech.details?.item?.installedCapacity);
    return sum + (roundedInstalledCapacityPeak || 0) + (roundedInstallCapacity || 0);
  }, 0);

  const hasBiogasOrHydrogen = normalizedProject.technologies.some(
    (tech: any) => tech.type === 'biogas' || tech.type === 'hydrogen'
  );

  const hasSolar = normalizedProject.technologies.some((tech: any) => tech.type === 'solar');
  const formattedCapacity = hasSolar
    ? `${europeanFormatNumber(totalInstalledCapacity)} MWp`
    : `${europeanFormatNumber(totalInstalledCapacity)} MW`;
  const location = useLocation({ location: [normalizedProject.location] });

  const fields = [
    {
      label: t('Technology'),
      value: technologies,
    },
    ...(!hasBiogasOrHydrogen
      ? [
          {
            label: t('Total Installed Capacity'),
            value: formattedCapacity ?? '—',
          },
        ]
      : []),
    {
      label: t('Location'),
      value: location,
    },
    {
      label: t('Other Details'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">
          {normalizedProject.otherDetails || t('No details available')}
        </span>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="folder-information" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('General Information')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {fields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>
    </div>
  );
}
